// MODIFIED: File has been significantly refactored and renamed.
// The original name was grip-settings-modal-manager.mixin.ts

import { Component, Vue, Watch } from 'vue-property-decorator';
import { ToastProgrammatic as Toast } from 'buefy';
import { Debounce } from '../../../jbi-shared/util/debounce.vue-decorator';
import {
  isDifferentDeep,
  isTruthy,
} from '../../../jbi-shared/util/watcher.vue-decorator';
import DeleteModalBox from '@/components/DeleteModalBox.vue';
import { ApiState, PaginatorDto } from '../../../store/types/general.types';

/**
 * A generic type definition for the entity data object.
 * We assume any entity passed to this mixin will have an 'id' property.
 */
type Entity = { id: number; [key: string]: any };

@Component
export default class GripSettingsModalManagerMixin extends Vue {
  // =================================================================
  // START: Abstract Properties & Computed Properties
  // =================================================================
  // These MUST be implemented by the component using this mixin.
  // They are used to configure the mixin for a specific entity.
  // -----------------------------------------------------------------

  /**
   * @abstract
   * The singular name of the entity (e.g., 'Barrier'). Used in UI messages.
   */
  public entityName: string = 'Entity';

  /**
   * @abstract
   * The key for the entity's ID when calling the delete action (e.g., 'barrierId').
   */
  public entityIdKey: string = 'entityId';

  /**
   * @abstract
   * The Vuex action function to fetch the list of entities.
   */
  public getAction!: (payload: any) => void;

  /**
   * @abstract
   * The Vuex action function to delete an entity.
   */
  public deleteAction!: (payload: any) => void;

  /**
   * @abstract
   * A computed property in the component that returns the API state for the GET action.
   * @example
   * get getApiState(): ApiState { return this.getGripBarriersApiState; }
   */
  public get getApiState(): ApiState {
    throw new Error('Must implement getApiState computed property.');
  }

  /**
   * @abstract
   * A computed property in the component that returns the API state for the CREATE action.
   */
  public get createApiState(): ApiState {
    throw new Error('Must implement createApiState computed property.');
  }

  /**
   * @abstract
   * A computed property in the component that returns the API state for the UPDATE action.
   */
  public get updateApiState(): ApiState {
    throw new Error('Must implement updateApiState computed property.');
  }

  /**
   * @abstract
   * A computed property in the component that returns the API state for the DELETE action.
   */
  public get deleteApiState(): ApiState {
    throw new Error('Must implement deleteApiState computed property.');
  }

  // =================================================================
  // END: Abstract Properties
  // =================================================================

  // --- MODIFIED: Data properties moved from components to the mixin ---
  public isModalActive: boolean = false;
  public isMinimized: boolean = false;
  public modalKey: number = Math.floor(Math.random() * 999);
  public editingEntityId: number | null = null;
  public entityDetailsForEdit: Entity | null = null;

  // --- NEW: Data properties for list management, moved from components ---
  public isLoading: boolean = true;
  public perPage: number = 50;
  public page: number = 1;
  public searchTitle: string = '';

  // --- NEW: Computed property for filter parameters ---
  get filteredParams() {
    return {
      title: this.searchTitle,
      limit: this.perPage,
      page: this.page,
    };
  }

  // --- NEW: Computed property to get the current project ID from the route ---
  get projectId(): number {
    return +this.$route.params.projectId;
  }

  private _resetLocalModalState(): void {
    this.isModalActive = false;
    this.isMinimized = false;
    this.editingEntityId = null;
    this.entityDetailsForEdit = null;
    this.modalKey += 1;
  }

  public closeModal(): void {
    this._resetLocalModalState();
  }

  public openModalForCreate(): void {
    this._resetLocalModalState();
    this.isModalActive = true;
  }

  public openModalForEdit(entity: Entity): void {
    this._resetLocalModalState();
    this.entityDetailsForEdit = entity;
    this.editingEntityId = entity.id;
    this.isModalActive = true;
  }

  public minimizeModal(): void {
    this.isMinimized = true;
  }

  public restoreModal(): void {
    this.isMinimized = false;
  }

  public onSaveSuccess(): void {
    this._resetLocalModalState();
  }

  // --- NEW: Generic data fetching method ---
  public fetchData(): void {
    this.getAction({
      projectId: this.projectId,
      params: this.filteredParams,
    });
  }

  // --- NEW: Generic method for handling pagination events ---
  public handlePaginator({ perPage, page }: PaginatorDto): void {
    this.page = page;
    this.perPage = perPage;
    // The watcher on 'filteredParams' will automatically trigger fetchData
  }

  // --- NEW: Generic method to request entity deletion ---
  public handleDeleteRequest(entityId: number): void {
    this.$buefy.modal.open({
      parent: this,
      component: DeleteModalBox,
      hasModalCard: true,
      trapFocus: true,
      props: {
        modalTitle: `Delete ${this.entityName}?`,
        modalContent: `Are you sure you want to delete this ${this.entityName.toLowerCase()}? This action cannot be undone.`,
      },
      events: {
        confirm: () => {
          this.deleteAction({
            projectId: this.projectId,
            [this.entityIdKey]: entityId,
          });
        },
      },
    });
  }

  // --- NEW: Watcher for search input ---
  @Watch('searchTitle')
  @Debounce(500)
  public onTitleSearch(): void {
    // Reset to page 1 on new search
    if (this.page !== 1) {
      this.page = 1;
    } else {
      // If already on page 1, the watcher for filteredParams will trigger the fetch
      this.fetchData();
    }
  }

  // --- NEW: Watcher for any change in pagination or search filters ---
  @Watch('filteredParams', { deep: true })
  @isDifferentDeep
  public watchFilteredParams(): void {
    this.fetchData();
  }

  // --- NEW: Generic API State Watchers ---

  @Watch('getApiState.loading')
  public watchGetLoading(isLoading: boolean): void {
    this.isLoading = isLoading;
  }

  @Watch('createApiState.success')
  @isTruthy
  public onCreateSuccess(): void {
    this.onSaveSuccess();
    this.fetchData();
    Toast.open({
      message: `${this.entityName} added`,
      position: 'is-top',
      type: 'is-dark',
      duration: 3500,
    });
  }

  @Watch('createApiState.error')
  public onCreateError(error: any): void {
    if (!error) return;
    Toast.open({
      message: `Failed to create ${this.entityName.toLowerCase()}.`,
      type: 'is-danger',
    });
  }

  @Watch('updateApiState.success')
  @isTruthy
  public onUpdateSuccess(): void {
    this.onSaveSuccess();
    this.fetchData();
    Toast.open({
      message: `${this.entityName} updated`,
      position: 'is-top',
      type: 'is-dark',
      duration: 3500,
    });
  }

  @Watch('updateApiState.error')
  public onUpdateError(error: any): void {
    if (!error) return;
    Toast.open({
      message: `Failed to update ${this.entityName.toLowerCase()}.`,
      type: 'is-danger',
    });
  }

  @Watch('deleteApiState.success')
  @isTruthy
  public onDeleteSuccess(): void {
    this.fetchData();
    Toast.open({
      message: `${this.entityName} deleted`,
      position: 'is-top',
      type: 'is-dark',
      duration: 3500,
    });
  }

  @Watch('deleteApiState.error')
  public onDeleteError(error: any): void {
    if (!error) return;
    Toast.open({
      message: `Failed to delete ${this.entityName.toLowerCase()}.`,
      type: 'is-danger',
      position: 'is-top',
      duration: 3500,
    });
  }

  // --- Lifecycle Hook ---
  public mounted(): void {
    this.fetchData();
  }
}
